import {OAuthDetails, SocialPlatform, Whitelist, SocialChannel} from '.';

export interface AuthedPageConfig {
    whitelist?: Partial<Whitelist>;
    oauthDetails?: OAuthDetails;
}

export interface AuthedPage extends SocialChannel {
    channelId?: string;
    isExistingPage?: boolean;
}

export interface AuthedResponse {
    platform?: SocialPlatform;
    pages?: AuthedPage[] | Partial<AuthedPage>[];
    oauthDetails?: OAuthDetails;
    errors?: any;
    state?: string;
}

export interface AuthState {
    state: string;
    platform: SocialPlatform;
    /** @deprecated */
    keys?: string;
    /** @deprecated */
    encryptedState?: string; // twitter
}

export interface AuthLink extends AuthState {
    redirectUrl: string;
}

export interface FacebookOAuthDetails extends Partial<OAuthDetails> {
    meta: {
        type: string;
        businessPages: {
            id: string;
            pageToken: string;
        }[];
    };
}

export interface InstagramBusinessAccount {
    username: string; // instagram user name
    name: string; // instagram display name
    id: string; // graph API id for IG account
    ig_id: string; // IG account ID
    followers_count: number;
}

//https://developers.facebook.com/docs/graph-api/reference/v8.0/user/accounts
export interface FacebookUserPage {
    id: string;
    is_owned: boolean;
    access_token: string;
    page_token: string;
    is_published?: boolean;
    followers_count?: number;
    link?: string;
    instagram_business_account?: Partial<InstagramBusinessAccount>;
}

export interface FacebookUserPages {
    data: FacebookUserPage[];
    paging: Record<string, any>;
    summary: Record<string, any>;
}

//https://developers.facebook.com/docs/graph-api/reference/insights-value/
export interface FacebookInsightValue {
    end_time: string; // datetime
    value: number | string | Record<string, any>;
}

export interface FacebookInsight {
    id: string;
    description: string;
    description_from_api_doc: string;
    name: string; // name of metric requested
    period: string;
    title: string;
    values: FacebookInsightValue[];
}

//https://developers.facebook.com/docs/graph-api/reference/v8.0/insights
export interface FacebookInsights {
    data: FacebookInsight[];
    paging: Record<string, any>;
    error?: string | Record<string, any>;
}

export interface InstagramAccount {
    instagram_business_account: InstagramBusinessAccount;
    id: string;
}

export interface InstagramInsights {
    biography: string;
    id: string;
    ig_id: string;
    followers_count: number;
    follows_count: number;
    media_count: number;
    name: string;
    profile_picture_url: string;
    username: string;
    website: string;
}

export enum TwitchUserType {
    STAFF = 'staff',
    ADMIN = 'admin',
    GLOBAL_MOD = 'global_mod',
    NONE = ''
}

export interface TwitchUser {
    id: string;
    login: string;
    display_name: string;
    type: TwitchUserType;
    broadcaster_type: string;
    description: string;
    profile_image_url: string;
    offline_image_url: string;
    view_count: number;
    email: string;
}

export interface TwitchUsers {
    data: TwitchUser[];
}

export interface TwitchUserFollowsData {
    from_id: string;
    from_name: string;
    to_id: string;
    to_name: string;
    followed_at: string;
}

export interface TwitchUserFollows {
    total: number;
    data: TwitchUserFollowsData[];
    pagination: {
        cursor: string;
    };
}

export interface OAuthTokens {
    access_token: string;
    expires_in: number; // milliseconds
    token_type: string; // Bearer
    scope: string | string[];
    refresh_token?: string; // Supported by Twitch, Youtube only
    // The machine_id is an optional parameter that identifies and tracks clients and is used for security and spam prevention
    // Reference : https://developers.facebook.com/docs/facebook-login/access-tokens/refreshing/#redeem-the-code-for-a-long-lived-access-token
    machine_id?: string;
}

export interface VKGroup {
    id: number;
    screen_name: string;
    type: string;
    is_closed: number; //0 is boolean;
    is_admin: number; //0 is boolean;
    admin_level: number; // (1 — moderator, 2 — editor, 3 — administrator).
    members_count: number;
}

export interface VKGroups {
    count: number;
    items: VKGroup[];
}

export interface VKResponse<T> {
    response: T;
    error?: {
        error_code: number;
        error_msg: string;
        request_params: {
            key: string;
            value: string;
        }[];
    };
}

export interface TwitterRequestToken {
    oauth_token: string;
    oauth_token_secret: string;
    oauth_callback_confirmed: boolean;
}

export interface TwitterToken {
    oauth_token: string;
    oauth_token_secret: string;
    user_id: number;
    screen_name: string;
}

// @doc https://developer.twitter.com/en/docs/twitter-api/v1/accounts-and-users/manage-account-settings/api-reference/get-account-verify_credentials
export interface TwitterProfile {
    contributors_enabled: boolean;
    created_at: string;
    default_profile: boolean;
    default_profile_image: boolean;
    description: string;
    favourites_count: number;
    follow_request_sent: null;
    followers_count: number;
    following: null;
    friends_count: number;
    geo_enabled: boolean;
    id: number;
    id_str: string;
    is_translator: boolean;
    lang: string;
    listed_count: number;
    location: string;
    name: string;
    notifications: null;
    profile_background_color: string;
    profile_background_image_url: string;
    profile_background_image_url_https: string;
    profile_background_tile: boolean;
    profile_image_url: string;
    profile_image_url_https: string;
    profile_link_color: string;
    profile_sidebar_border_color: string;
    profile_sidebar_fill_color: string;
    profile_text_color: string;
    profile_use_background_image: boolean;
    protected: boolean;
    screen_name: string;
    show_all_inline_media: boolean;
    status?: {
        contributors: null;
        coordinates: {
            coordinates: number[];
            type: string;
        };
        created_at: string;
        favorited: boolean;
        geo: {
            coordinates: number[];
            type: string;
        };
        id: number;
        id_str: string;
        in_reply_to_screen_name: string;
        in_reply_to_status_id: number;
        in_reply_to_status_id_str: string;
        in_reply_to_user_id: number;
        in_reply_to_user_id_str: string;
        place: {
            attributes: Record<string, any>;
            bounding_box: {
                coordinates: number[][][];
                type: string;
            };
            country: string;
            country_code: string;
            full_name: string;
            id: string;
            name: string;
            place_type: string;
            url: string;
        };
        retweet_count: number;
        retweeted: boolean;
        source: string;
        text: string;
        truncated: boolean;
    };
    statuses_count: number;
    time_zone: string;
    url: null;
    utc_offset: number;
    verified: boolean;
    translator_type: string;
    suspended: boolean;
    needs_phone_verification: boolean;
}
